import { useParams, useNavigate } from 'react-router';
import { useState, useEffect, useRef } from 'react';
import { isAxiosError } from 'axios';
import { ArrowLeft, ExternalLink, History, Loader2, Pencil, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Editor } from '@/components/ui/editor';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { CategoryCombobox } from '@/modules/sop/components/category-combobox';
import {
	useSOPById,
	useSOPMarkdown,
	useCreateSOP,
	useUpdateSOP,
	useRenameSOP,
	useApproveRejectSOP,
	useSopCategoryTree
} from '@/hooks';
import { useBuildProtocol } from '@/hooks/use-sops';
import { usePermissions } from '@/hooks/use-permissions';
import { invalidateQueries } from '@/lib/query-client';
import { getApiErrorMessage } from '@/lib/utils';
import { getSopStatusDisplay, hasUnpublishedSopDraft, shouldOfferSopPublish, sopEditorDisplayMarkdown } from '@/modules/sop';
import { DecisionHistoryPanel } from './decision-history-panel';
import { computeContentHash } from '@/lib/content-hash';

/** True for an HTTP 409 draft conflict — the SOP changed under us since we loaded it. */
function isDraftConflict(err: unknown): boolean {
	return isAxiosError(err) && err.response?.status === 409;
}

export default function SOPDetailRoute() {
	const { id } = useParams();
	const navigate = useNavigate();
	const isNew = !id || id === 'new';

	// Step 1: fetch SOP metadata + previewUrl
	const { data: sopData, isLoading: isSopLoading } = useSOPById(isNew ? '' : id!);

	// Step 2: fetch the body from its presigned URL. Prefer the draft when one is open:
	// editing should show what you last saved, not the version customers are being served.
	const { data: markdownContent, isLoading: isMarkdownLoading } = useSOPMarkdown(
		sopData?.draftPreviewUrl ?? sopData?.previewUrl ?? ''
	);

	const createSOPMutation = useCreateSOP();
	const updateSOPMutation = useUpdateSOP();
	const renameSOPMutation = useRenameSOP();
	const approveRejectMutation = useApproveRejectSOP();
	const { can } = usePermissions();
	const canEdit = can('edit', 'sop');
	const canApprove = can('edit', 'sop.approve');
	const canViewWorkflow = can('view', 'workflow');
	// Temporary email allowlist (VITE_SOP_V2_ALLOWED_EMAILS) on top of the normal
	// permission — gates only the V2 generation entry points, nothing else.

	const [isEditing, setIsEditing] = useState(isNew);
	const [name, setName] = useState('');
	const [content, setContent] = useState('');
	// A new SOP is filed under a category / subcategory (the backend requires both); an
	// existing one keeps its categorisation, so these only matter while `isNew`.
	const [category, setCategory] = useState('');
	const [subcategory, setSubcategory] = useState('');
	const { data: categoryTree = [] } = useSopCategoryTree();
	const categoryOptions = Array.from(new Set(categoryTree.map((c) => c.category))).sort();
	const subcategoryOptions = Array.from(
		new Set(
			(categoryTree.find((c) => c.category === category)?.subcategories ?? categoryTree.flatMap((c) => c.subcategories)).map(
				(s) => s.name
			)
		)
	).sort();
	/** Last successfully saved draft — used so the page does not snap back to the live body after save. */
	const [savedDraftContent, setSavedDraftContent] = useState('');
	// One docked panel at a time — opening either closes the other.
	const [dockedPanel, setDockedPanel] = useState<'none' | 'history'>('none');
	const historyOpen = dockedPanel === 'history';
	// Hash of the body we last loaded or saved — the optimistic-concurrency guard that
	// stops two people silently overwriting each other's draft.
	const baseHashRef = useRef<string | undefined>(undefined);
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [showDiscardDialog, setShowDiscardDialog] = useState(false);
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const [pendingSaveAction, setPendingSaveAction] = useState<'draft' | 'publish' | null>(null);
	const loadedSOPIdRef = useRef<string | undefined>(undefined);

	const sop = sopData?.sop;
	/**
	 * An open draft the published version does not yet include.
	 *
	 * Editing a published SOP deliberately leaves it live, which is right — a typo fix must
	 * not take a SOP out of service. But it also meant the page looked identical before and
	 * after an edit, so nothing told you that what customers are served, and the workflow
	 * generated from it, are now behind your draft. The backend already says so by returning
	 * a separate draft preview; this surfaces it.
	 */
	const hasUnpublishedChanges = hasUnpublishedSopDraft(sopData ?? {});
	const originalName = isNew ? '' : (sop?.intent.replace(/_/g, ' ') ?? '');
	const originalContent = savedDraftContent || (markdownContent ?? '');
	const hasUnsavedChanges = isEditing && (content !== originalContent || name.trim() !== originalName.trim());

	useEffect(() => {
		if (sop && sop.id !== loadedSOPIdRef.current) {
			setName(sop.intent.replace(/_/g, ' '));
			setSavedDraftContent('');
			loadedSOPIdRef.current = sop.id;
		}
	}, [sopData, sop]);

	// Track the loaded body's hash so a save can tell whether it is overwriting someone
	// else's work. Recomputed after every successful save, not just on load.
	useEffect(() => {
		if (markdownContent === undefined) return;
		void computeContentHash(markdownContent).then((hash) => {
			baseHashRef.current = hash;
		});
	}, [markdownContent]);

	const handleEdit = () => {
		setContent(savedDraftContent || (markdownContent ?? ''));
		setName(originalName);
		setIsEditing(true);
	};

	const discardEdits = () => {
		if (isNew) {
			navigate('/training/protocols?view=drafts');
			return;
		}
		setContent(originalContent);
		setName(originalName);
		setIsEditing(false);
	};

	const handleCancel = () => {
		if (hasUnsavedChanges) {
			setShowDiscardDialog(true);
			return;
		}
		discardEdits();
	};

	const confirmDiscard = () => {
		setShowDiscardDialog(false);
		discardEdits();
	};

	// Renames only fire for an existing SOP — for a brand-new one, the typed name is sent as
	// part of the create payload below instead (there's no SOP yet to rename).
	const renameIfChanged = async () => {
		if (isNew || !id) return;
		const trimmedName = name.trim();
		if (!trimmedName || trimmedName === originalName.trim()) return;
		await renameSOPMutation.mutateAsync({ id, intent: trimmedName });
	};

	const handleSaveDraft = async () => {
		try {
			if (isNew) {
				const created = await createSOPMutation.mutateAsync({ title: name.trim(), category, subcategory, content });
				toast.success('Draft created');
				// /sops/new and /sops/:id render the same component, so leave edit mode here or
				// the new SOP opens still in the editor.
				setIsEditing(false);
				navigate(`/sops/${created.id}`, { replace: true });
			} else {
				await renameIfChanged();
				const wasPublished = approvalStatus === 'approved';
				await updateSOPMutation.mutateAsync({
					id: id!,
					data: { content, status: 'draft', expectedContentHash: baseHashRef.current }
				});
				baseHashRef.current = await computeContentHash(content);
				setSavedDraftContent(content);
				// Saving a draft no longer takes a published SOP out of service — the published
				// version keeps serving until it is explicitly published again.
				toast.success(
					wasPublished ? 'Draft saved — the published version is still live' : 'Draft saved successfully'
				);
				setIsEditing(false);
			}
			setShowSaveDialog(false);
		} catch (error) {
			toast.error(
				isDraftConflict(error)
					? 'This draft was edited elsewhere. Reload to get the latest version before saving.'
					: 'Failed to save draft'
			);
		}
	};

	const handleSaveAndPublish = async () => {
		try {
			if (isNew) {
				// The upload endpoint always creates a pending SOP; publishing is the approval step.
				const created = await createSOPMutation.mutateAsync({ title: name.trim(), category, subcategory, content });
				await approveRejectMutation.mutateAsync({ id: created.id, approvalStatus: 'approved' });
				toast.success('Published — building the protocol');
				setIsEditing(false);
				navigate(`/sops/${created.id}`, { replace: true });
			} else {
				await renameIfChanged();
				await updateSOPMutation.mutateAsync({
					id: id!,
					data: { content, status: 'published', expectedContentHash: baseHashRef.current }
				});
				await approveRejectMutation.mutateAsync({ id: id!, approvalStatus: 'approved' });
				baseHashRef.current = await computeContentHash(content);
				setSavedDraftContent('');
				toast.success('Published — building the protocol');
				setIsEditing(false);
			}
			setShowSaveDialog(false);
		} catch (error) {
			// A 409 here is ambiguous by status alone: it is either a concurrent edit or an
			// already-approved SOP, and telling the operator the wrong one sends them the
			// wrong way. Distinguish on the message the backend sends.
			const message = isAxiosError(error) ? getApiErrorMessage(error) : '';
			toast.error(
				isDraftConflict(error)
					? /already/i.test(message)
						? 'Already published'
						: 'This draft was edited elsewhere. Reload to get the latest version before publishing.'
					: isAxiosError(error) && error.response?.status === 403
						? 'You do not have permission to publish this draft'
						: 'Failed to publish'
			);
		}
	};

	const handleConfirmSave = () => {
		if (pendingSaveAction === 'publish') return handleSaveAndPublish();
		return handleSaveDraft();
	};

	const requestSave = (action: 'draft' | 'publish') => {
		if (isNew) {
			if (!name.trim()) return void toast.error('Give the draft a title before saving');
			if (!category.trim() || !subcategory.trim()) return void toast.error('Choose a category and subcategory before saving');
			if (!content.trim()) return void toast.error('Write the draft before saving');
		}
		setPendingSaveAction(action);
		setShowSaveDialog(true);
	};

	const handlePublish = async () => {
		try {
			await approveRejectMutation.mutateAsync({ id: id!, approvalStatus: 'approved' });
			setSavedDraftContent('');
			toast.success(sop?.approvalStatus === 'approved' ? 'Changes published' : 'Published — building the protocol');
		} catch (error) {
			const status = isAxiosError(error) ? error.response?.status : undefined;
			const message =
				status === 403
					? 'You do not have permission to publish this draft'
					: status === 409
						? 'Draft already reviewed'
						: status === 422
							? 'This draft cannot be published'
							: 'Failed to publish';
			toast.error(message);
		}
	};

	const handleReject = async () => {
		try {
			await approveRejectMutation.mutateAsync({ id: id!, approvalStatus: 'rejected' });
			toast.success('Draft rejected');
			setRejectDialogOpen(false);
		} catch (error) {
			const message = isAxiosError(error) && error.response?.status === 409 ? 'Draft already reviewed' : 'Failed to reject the draft';
			toast.error(message);
		}
	};

	const isSaving = createSOPMutation.isPending || updateSOPMutation.isPending;
	const isApproving = approveRejectMutation.isPending;
	const isLoading = !isNew && (isSopLoading || isMarkdownLoading);

	const sopStatus = sop?.status ?? 'draft';
	const approvalStatus = sop?.approvalStatus ?? 'pending';
	const workflowStatus = sop?.workflowStatus ?? 'none';
	const workflowId = sop?.workflowId;
	const workflowError = sop?.workflowError;
	const failedWorkflowReason = workflowError ?? 'The protocol could not be built. Try again.';
	/**
	 * A published draft is its protocol's text from then on: read here, edited there. The
	 * backend refuses edits to it; this page offers the protocol instead.
	 */
	const linkedWorkflowId = workflowStatus === 'created' ? workflowId : undefined;
	// Opened where the Protocols list opens every row, whatever its type.
	const linkedPath = linkedWorkflowId && `/training/protocols/${linkedWorkflowId}`;
	const buildProtocolMutation = useBuildProtocol();
	const handleBuildProtocol = async () => {
		if (!id) return;
		try {
			await buildProtocolMutation.mutateAsync(id);
			toast.success('Building the protocol…');
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not start building the protocol'));
		}
	};
	const isWorkflowGenerating = workflowStatus === 'generating';
	const statusConfig = getSopStatusDisplay({ status: sopStatus, approvalStatus });
	const displayTitle = isNew ? name : (sop?.intent?.replace(/_/g, ' ') ?? name ?? 'Untitled draft');

	// A protocol build has no background job to watch — poll the draft until it finishes.
	useEffect(() => {
		if (!isWorkflowGenerating || !id) return;
		const interval = setInterval(() => invalidateQueries(['sops', id]), 3000);
		return () => clearInterval(interval);
	}, [isWorkflowGenerating, id]);

	if (isLoading) {
		return (
			<div className='flex h-screen overflow-hidden'>
				<div className='flex-1 flex flex-col overflow-hidden'>
					<div className='flex items-center gap-4 p-4 border-b'>
						<Skeleton className='h-8 w-8 rounded' />
						<Skeleton className='h-5 w-48' />
						<Skeleton className='h-5 w-20 rounded-full' />
					</div>
					<div className='px-15 pt-8 space-y-4'>
						<Skeleton className='h-6 w-1/3' />
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-2/3' />
						<Skeleton className='h-6 w-1/4 mt-6' />
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-full' />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='flex h-screen overflow-hidden'>
			<div className='flex-1 flex flex-col overflow-hidden'>
				{/* Header */}
				<div className='flex items-center gap-4 p-4 border-b'>
					<div className='relative flex-1 flex items-center gap-3 h-8'>
						{/* navigate(-1) instead of a hardcoded '/sources?tab=sops' — a fixed URL would drop
						whatever category/subcategory/page filter was active on the list you came from,
						same issue the browser Back button had before it was fixed to preserve that state
						via the URL. */}
						<Button
							className='cursor-pointer border-none shadow-none'
							variant='outline'
							size='icon'
							onClick={() => navigate(-1)}
						>
							<ArrowLeft className='size-5' />
						</Button>
						{isEditing ? (
							<>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder='Untitled draft'
									className='h-8 max-w-sm text-base font-semibold'
								/>
								{isNew && (
									<>
										<div className='w-44 shrink-0'>
										<CategoryCombobox
											value={category}
											onChange={(value) => {
												setCategory(value);
												setSubcategory('');
											}}
											options={categoryOptions}
											placeholder='Category'
										/>
										</div>
										<div className='w-44 shrink-0'>
										<CategoryCombobox
											value={subcategory}
											onChange={setSubcategory}
											options={subcategoryOptions}
											placeholder='Subcategory'
											lockedMessage={category ? undefined : 'Choose a category first'}
										/>
										</div>
									</>
								)}
							</>
						) : (
							<>
								<div className='p-2 rounded-md text-base font-semibold capitalize'>{displayTitle}</div>
								{!isNew && (
									<Badge
										variant='outline'
										className={`text-xs font-medium px-3 py-0.5 rounded-full border-0 ${statusConfig.bgColor} ${statusConfig.textColor}`}
									>
										{statusConfig.label}
									</Badge>
								)}
								{!isNew && hasUnpublishedChanges && (
									<Tooltip>
										<TooltipTrigger asChild>
											<Badge
												variant='outline'
												className='rounded-full border-0 bg-amber-50 px-3 py-0.5 text-xs font-medium text-amber-700'
											>
												Unpublished changes
											</Badge>
										</TooltipTrigger>
										<TooltipContent side='bottom' className='max-w-xs'>
											You have edits saved as a draft. The published version is still what customers are
											served, and the workflow was generated from it — publish to apply your changes and
											regenerate.
										</TooltipContent>
									</Tooltip>
								)}
								{!isNew && !linkedPath && sop?.qualityScore != null && (
									<div className='flex items-center gap-1.5 text-xs text-gray-500'>
										<span>Quality</span>
										<span className='font-semibold text-gray-700'>{sop.qualityScore}/10</span>
									</div>
								)}
							</>
						)}
					</div>
					<div className='flex items-center gap-2 h-8'>
						{isEditing ? (
							<>
								<Button className='text-xs' variant='outline' onClick={handleCancel} disabled={isSaving}>
									Cancel
								</Button>
								{canEdit && (
									<Button className='text-xs' variant='outline' onClick={() => requestSave('draft')} disabled={isSaving || !name.trim()}>
										{approvalStatus === 'approved' ? 'Save' : 'Save Draft'}
									</Button>
								)}
								{canApprove && (
									<Button className='text-xs' onClick={() => requestSave('publish')} disabled={isSaving || !name.trim()}>
										Save & Publish
									</Button>
								)}
							</>
						) : isNew ? (
							<>
								{canEdit && (
									<>
										<Button className='cursor-pointer text-xs' variant='outline' onClick={handleEdit}>
											<Pencil className='h-3 w-3' /> Edit
										</Button>
										<Button className='text-xs' onClick={handleSaveDraft} disabled={isSaving}>
											Save Draft
										</Button>
										<Button className='text-xs' onClick={handleSaveAndPublish} disabled={isSaving}>
											Save & Publish
										</Button>
									</>
								)}
							</>
						) : approvalStatus === 'pending' ? (
							<>
								{canEdit && (
									<Button className='cursor-pointer text-xs' variant='outline' onClick={handleEdit}>
										<Pencil className='h-3 w-3' /> Edit
									</Button>
								)}
								{canApprove && (
									<>
										<Button className='text-xs' variant='outline' onClick={() => setRejectDialogOpen(true)} disabled={isApproving}>
											Reject
										</Button>
										<Button className='text-xs' onClick={() => void handlePublish()} disabled={isApproving}>
											{isApproving ? 'Publishing…' : 'Publish as protocol'}
										</Button>
									</>
								)}
							</>
						) : approvalStatus === 'approved' && workflowStatus === 'created' ? (
							// A protocol's SOP is its text: the one thing to do from here is open the protocol.
							linkedPath && (
								<Button className='text-xs' onClick={() => navigate(linkedPath)}>
									<ExternalLink className='h-3 w-3' /> Open protocol
								</Button>
							)
						) : approvalStatus === 'approved' && isWorkflowGenerating ? (
							<>
								<Button className='text-xs' disabled>
									<Loader2 className='size-3 animate-spin' />
									Building protocol…
								</Button>
							</>
						) : approvalStatus === 'approved' && workflowStatus === 'failed' ? (
							<>
								{canEdit && (
									<Button className='cursor-pointer text-xs' variant='outline' onClick={handleEdit}>
										<Pencil className='h-3 w-3' /> Edit
									</Button>
								)}
								{canViewWorkflow && (
									<Tooltip>
										<TooltipTrigger asChild>
											<span tabIndex={0}>
												<Button className='text-xs' variant='outline' disabled>
													Protocol not built
												</Button>
											</span>
										</TooltipTrigger>
										<TooltipContent side='bottom' className='max-w-xs whitespace-pre-wrap'>
											{failedWorkflowReason}
										</TooltipContent>
									</Tooltip>
								)}
								{canViewWorkflow && (
									<Button
										className='text-xs'
										variant='outline'
										onClick={() => setDockedPanel('history')}
										title='Every question the generator asked, and how it was answered'
									>
										<History className='h-3 w-3' /> History
									</Button>
								)}
								{canApprove && (
									<Tooltip>
										<TooltipTrigger asChild>
											<span tabIndex={0}>
												<Button
													className='text-xs'
													onClick={() => void handleBuildProtocol()}
													disabled={buildProtocolMutation.isPending || hasUnpublishedChanges}
												>
													<RotateCcw className='h-3 w-3' /> Try again
												</Button>
											</span>
										</TooltipTrigger>
										{hasUnpublishedChanges && (
											<TooltipContent side='bottom'>Publish your draft first — the protocol is built from what is published</TooltipContent>
										)}
									</Tooltip>
								)}
							</>
						) : approvalStatus === 'approved' && workflowStatus === 'none' ? (
							<>
								{canEdit && (
									<Button className='cursor-pointer text-xs' variant='outline' onClick={handleEdit}>
										<Pencil className='h-3 w-3' /> Edit
									</Button>
								)}
								{canViewWorkflow && (
									<Button
										className='text-xs'
										variant='outline'
										onClick={() => setDockedPanel('history')}
										title='Every question the generator asked, and how it was answered'
									>
										<History className='h-3 w-3' /> History
									</Button>
								)}
								{canApprove && (
									<Tooltip>
										<TooltipTrigger asChild>
											<span tabIndex={0}>
												<Button
													className='text-xs'
													onClick={() => void handleBuildProtocol()}
													disabled={buildProtocolMutation.isPending || hasUnpublishedChanges}
												>
													Publish as protocol
												</Button>
											</span>
										</TooltipTrigger>
										{hasUnpublishedChanges && (
											<TooltipContent side='bottom'>Publish your draft first — the protocol is built from what is published</TooltipContent>
										)}
									</Tooltip>
								)}
							</>
						) : approvalStatus === 'rejected' ? (
							<>
								<Badge variant='outline' className='text-xs font-medium px-3 py-0.5 rounded-full border-0 bg-red-50 text-red-700'>
									Rejected
								</Badge>
							</>
						) : null}
						{canApprove &&
							!isEditing &&
							!isWorkflowGenerating &&
							shouldOfferSopPublish({ approvalStatus, isEditing: false, hasUnpublishedChanges }) && (
								<Button className='text-xs' onClick={() => void handlePublish()} disabled={isApproving}>
									{isApproving ? 'Publishing…' : linkedWorkflowId ? 'Publish update' : 'Publish as protocol'}
								</Button>
							)}
					</div>
				</div>

				{/* Editor Content */}
					<div className='flex-1 overflow-y-auto pt-8 px-15 pb-8'>
						<Editor
							placeholder='Start writing...'
							value={sopEditorDisplayMarkdown({
								isEditing,
								draftMarkdown: isEditing ? content : savedDraftContent,
								loadedMarkdown: markdownContent ?? '',
								hasUnpublishedChanges
							})}
							contentType='markdown'
							onChange={(newContent) => setContent(newContent)}
							readOnly={!isEditing}
							showToolbar={isEditing}
							className='border-0! ring-0 rounded-none'
						/>
					</div>
			</div>

			{/* Docked as well, and never at the same time as the generator: two 420px panels
			    beside each other leave nothing of the SOP to read. */}
			{!isNew && id && (
				<DecisionHistoryPanel sopId={id} open={historyOpen} onClose={() => setDockedPanel('none')} />
			)}


			<Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
				<DialogContent className='max-w-sm p-4'>
					<DialogHeader>
						<DialogTitle className='text-sm font-semibold'>Reject draft?</DialogTitle>
						<p className='text-xs text-muted-foreground'>Reject this draft and send it back for revisions.</p>
					</DialogHeader>
					<div className='flex gap-2 justify-end'>
						<Button variant='outline' className='text-xs' onClick={() => setRejectDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							variant='outline'
							className='text-xs text-red-500 border-red-200 hover:text-red-500 hover:bg-red-50'
							onClick={handleReject}
							disabled={isApproving}
						>
							{isApproving ? 'Rejecting…' : 'Reject'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
				<DialogContent className='max-w-sm p-4'>
					<DialogHeader>
						<DialogTitle className='text-sm font-semibold'>Discard changes?</DialogTitle>
						<p className='text-xs text-muted-foreground'>
							You have unsaved changes. If you leave now, they&apos;ll be lost. Are you sure you want to cancel?
						</p>
					</DialogHeader>
					<div className='flex gap-2 justify-end'>
						<Button variant='outline' className='text-xs' onClick={confirmDiscard}>
							Discard
						</Button>
						<Button className='text-xs' onClick={() => setShowDiscardDialog(false)}>
							Keep Editing
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
				<DialogContent className='max-w-sm p-4'>
					<DialogHeader>
						<DialogTitle className='text-sm font-semibold'>
							{pendingSaveAction === 'publish' ? 'Publish changes?' : 'Save changes?'}
						</DialogTitle>
						<p className='text-xs text-muted-foreground'>
							{pendingSaveAction === 'publish'
								? 'Publishing builds the protocol from your edits.'
								: approvalStatus === 'approved'
									? 'This saves a draft. The published version stays live until you publish.'
									: 'Are you sure you want to save these changes?'}
						</p>
					</DialogHeader>
					<div className='flex gap-2 justify-end'>
						<Button variant='outline' className='text-xs' onClick={() => setShowSaveDialog(false)} disabled={isSaving}>
							Cancel
						</Button>
						<Button className='text-xs' onClick={handleConfirmSave} disabled={isSaving}>
							{isSaving ? 'Saving…' : pendingSaveAction === 'publish' ? 'Publish' : 'Save'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
